import React from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useUIStore } from '../stores/useUIStore';
import { Image, Maximize2, FileCode } from 'lucide-react';

export interface GalleryItem {
  id: string;
  title: string;
  docName: string;
  code?: string;
  dataUrl?: string;
  isMermaid: boolean;
}

export const VisualGalleryView: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { openLightbox } = useUIStore();

  // 1. Extract all inline mermaid code blocks across all documents
  const mermaidItems: GalleryItem[] = currentPackage?.documents.flatMap((doc) => {
    const blocks: GalleryItem[] = [];
    const matches = doc.content.match(/```mermaid[\s\S]*?```/g) || [];
    matches.forEach((match, idx) => {
      const code = match.replace('```mermaid\n', '').replace('\n```', '');
      blocks.push({
        id: `${doc.name}-diagram-${idx}`,
        title: `Diagram ${idx + 1} (${doc.name})`,
        docName: doc.name,
        code,
        isMermaid: true
      });
    });
    return blocks;
  }) || [];

  // 2. Extract all image/diagram asset files located in subfolders
  const assetItems: GalleryItem[] = currentPackage?.assets?.map((asset, idx) => {
    const item: GalleryItem = {
      id: `asset-${idx}`,
      title: `${asset.name} (${asset.folderPath})`,
      docName: asset.folderPath,
      isMermaid: false
    };
    if (asset.dataUrl) {
      item.dataUrl = asset.dataUrl;
    }
    return item;
  }) || [];

  const allGalleryItems: GalleryItem[] = [...mermaidItems, ...assetItems];

  return (
    <div className="flex-1 overflow-y-auto p-8 max-w-6xl mx-auto space-y-6">
      {/* Gallery Header */}
      <div className="glass-panel p-6 rounded-2xl border border-white/10 flex items-center justify-between shadow-2xl">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Image className="w-5 h-5 text-cyan-400" />
            Visual Architecture & Diagram Gallery
          </h2>
          <p className="text-xs text-slate-400 mt-1 font-mono">
            Visual schematics, subfolder assets, and sequence charts across {currentPackage?.name || 'Workspace'}.
          </p>
        </div>

        <div className="text-xs font-mono px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
          {allGalleryItems.length} Visual Assets
        </div>
      </div>

      {/* Gallery Grid */}
      {allGalleryItems.length === 0 ? (
        <div className="glass-panel p-12 rounded-2xl text-center text-xs font-mono text-slate-500 flex flex-col items-center space-y-2">
          <FileCode className="w-8 h-8 text-slate-600 mb-1" />
          <span>No embedded diagrams or visual assets found in this specification package.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {allGalleryItems.map((item) => (
            <div key={item.id} className="glass-panel rounded-2xl border border-white/10 overflow-hidden space-y-3 p-5 group hover:border-cyan-500/40 transition-all shadow-xl">
              <div className="flex items-center justify-between border-b border-white/5 pb-2 text-xs font-mono">
                <span className="text-slate-200 font-semibold truncate">{item.title}</span>
                <button
                  onClick={() => {
                    if (item.isMermaid && item.code) {
                      openLightbox(`<svg width="100%" height="100%"><text x="20" y="40" fill="#06b6d4" font-size="16">${item.code}</text></svg>`, item.title);
                    } else if (item.dataUrl) {
                      openLightbox(`<img src="${item.dataUrl}" style="max-width:100%; height:auto;" />`, item.title);
                    }
                  }}
                  className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center space-x-1.5 transition-colors text-[11px] border border-slate-700"
                >
                  <Maximize2 className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Lightbox</span>
                </button>
              </div>

              {item.isMermaid ? (
                <pre className="text-xs font-mono text-cyan-200 bg-obsidian-950 p-4 rounded-xl overflow-x-auto max-h-48 border border-slate-800">
                  {item.code}
                </pre>
              ) : (
                <div className="bg-obsidian-950 p-4 rounded-xl border border-slate-800 flex items-center justify-center max-h-48 overflow-hidden">
                  <img src={item.dataUrl} alt={item.title} className="max-h-40 object-contain rounded" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
